import { html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import Styles from './vm-prompt-input.scss';

const PLACEHOLDER_TEXTS = [
    'Compare treatment options for...',
    'Summarize recent trials on...',
    'What are the latest guidelines for...',
    'What does the evidence say about...',
    'What do experts recommend for...',
];

const TYPE_SPEED = 50;
const PAUSE_AFTER_TYPE = 3600;
const FADE_DURATION = 430;

const MOBILE_QUERY = '(max-width: 767px)';

type TypewriterPhase = 'typing' | 'pausing' | 'fading';

/**
 * A multi-line prompt input with an autosizing textarea, animated typewriter
 * placeholder, and a send/stop submit button.
 *
 * Fires `vm-send` with `detail.message` when the user submits. Fires `vm-stop`
 * when the stop button is clicked while `is-streaming` is true.
 *
 * @fires vm-send - CustomEvent<{ message: string }>
 * @fires vm-stop - CustomEvent<void>
 */
@customElement('vm-prompt-input')
export class VmPromptInput extends LitElement {
    static override styles = Styles;

    /** Disable input and submission. */
    @property({ type: Boolean, reflect: true })
    disabled = false;

    /** Static placeholder text. When set, the rotating typewriter placeholder is suppressed. */
    @property()
    placeholder?: string;

    /** Compact horizontal layout (used inside an active chat window). */
    @property({ type: Boolean, reflect: true })
    compact = false;

    /** Auto-focus the textarea on first connect. */
    @property({ type: Boolean, attribute: 'auto-focus' })
    autoFocusOnMount = false;

    /** Hide the legal disclaimer beneath the form. */
    @property({ type: Boolean, attribute: 'hide-disclaimer' })
    hideDisclaimer = false;

    /** Replace the submit button with a stop button. */
    @property({ type: Boolean, attribute: 'is-streaming', reflect: true })
    isStreaming = false;

    @state()
    private _value = '';

    @state()
    private _userFocused = false;

    @state()
    private _typewriterText = '';

    @state()
    private _typewriterFading = false;

    @state()
    private _isMobile = window.matchMedia(MOBILE_QUERY).matches;

    @query('textarea')
    private _textarea!: HTMLTextAreaElement;

    private _mql = window.matchMedia(MOBILE_QUERY);
    private _typewriterTimer: ReturnType<typeof setTimeout> | null = null;
    private _typewriterState = { index: 0, charPos: 0, phase: 'typing' as TypewriterPhase };

    private get _isCompact() {
        return this.compact && this._isMobile;
    }

    private get _showTypewriter() {
        return !this._value && !this.placeholder && !this._userFocused;
    }

    override connectedCallback(): void {
        super.connectedCallback();
        this._mql.addEventListener('change', this._onBreakpointChange);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._mql.removeEventListener('change', this._onBreakpointChange);
        this._stopTypewriter();
    }

    override firstUpdated(): void {
        if (this.autoFocusOnMount) {
            this._textarea.focus();
        }
        this._resizeTextarea();
        if (this._showTypewriter) this._startTypewriter();
    }

    override updated(changed: Map<PropertyKey, unknown>): void {
        if (
            changed.has('_value') ||
            changed.has('placeholder') ||
            changed.has('_userFocused') ||
            changed.has('_isMobile') ||
            changed.has('compact')
        ) {
            this._resizeTextarea();
        }

        if (changed.has('_value') || changed.has('placeholder') || changed.has('_userFocused')) {
            if (this._showTypewriter) {
                this._startTypewriter();
            } else {
                this._stopTypewriter();
            }
        }
    }

    private _onBreakpointChange = (e: MediaQueryListEvent): void => {
        this._isMobile = e.matches;
    };

    private _resizeTextarea() {
        const el = this._textarea;
        if (!el) return;
        const minHeight = this._isCompact ? 24 : 48;
        el.style.height = 'auto';
        el.style.height = Math.max(el.scrollHeight, minHeight) + 'px';
    }

    private _startTypewriter() {
        this._stopTypewriter();
        this._typewriterState = { index: 0, charPos: 0, phase: 'typing' };
        this._typewriterText = '';
        this._typewriterFading = false;
        this._typewriterTimer = setTimeout(() => this._tickTypewriter(), TYPE_SPEED);
    }

    private _stopTypewriter() {
        if (this._typewriterTimer !== null) {
            clearTimeout(this._typewriterTimer);
            this._typewriterTimer = null;
        }
    }

    private _tickTypewriter() {
        const s = this._typewriterState;
        const fullText = PLACEHOLDER_TEXTS[s.index] ?? '';

        switch (s.phase) {
            case 'typing':
                s.charPos++;
                this._typewriterText = fullText.slice(0, s.charPos);
                if (s.charPos >= fullText.length) {
                    s.phase = 'pausing';
                    this._typewriterTimer = setTimeout(() => this._tickTypewriter(), PAUSE_AFTER_TYPE);
                } else {
                    this._typewriterTimer = setTimeout(() => this._tickTypewriter(), TYPE_SPEED);
                }
                break;

            case 'pausing':
                s.phase = 'fading';
                this._typewriterFading = true;
                this._typewriterTimer = setTimeout(() => this._tickTypewriter(), FADE_DURATION);
                break;

            case 'fading':
                s.index = (s.index + 1) % PLACEHOLDER_TEXTS.length;
                s.charPos = 0;
                s.phase = 'typing';
                this._typewriterText = '';
                this._typewriterFading = false;
                this._typewriterTimer = setTimeout(() => this._tickTypewriter(), TYPE_SPEED);
                break;
        }
    }

    private _handleInput = (e: Event) => {
        this._value = (e.target as HTMLTextAreaElement).value;
    };

    private _handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this._submit();
        }
    };

    private _handlePointerDown = () => {
        this._userFocused = true;
    };

    private _handleBlur = () => {
        this._userFocused = false;
    };

    private _handleSubmit = (e: Event) => {
        e.preventDefault();
        this._submit();
    };

    private _submit() {
        const trimmed = this._value.trim();
        if (!trimmed || this.disabled || this.isStreaming) return;
        this.dispatchEvent(
            new CustomEvent('vm-send', {
                detail: { message: trimmed },
                bubbles: true,
                composed: true,
            }),
        );
        this._value = '';
    }

    private _handleStop = () => {
        this.dispatchEvent(new CustomEvent('vm-stop', { bubbles: true, composed: true }));
    };

    override render() {
        const showTypewriter = this._showTypewriter;
        const canSend = this._value.trim().length > 0 && !this.disabled;

        return html`
            <form class="form" @submit=${this._handleSubmit}>
                <div class="textarea-wrapper">
                    ${showTypewriter
                        ? html`
                              <div class="typewriter">
                                  <vm-icon-next class="typewriter__icon" name="sparkle" size="sm"></vm-icon-next>
                                  <span class=${this._typewriterFading ? 'typewriter__text--fading' : ''}>
                                      ${this._typewriterText}
                                  </span>
                              </div>
                          `
                        : nothing}
                    <textarea
                        class="textarea"
                        .value=${this._value}
                        @input=${this._handleInput}
                        @keydown=${this._handleKeyDown}
                        @pointerdown=${this._handlePointerDown}
                        @blur=${this._handleBlur}
                        placeholder=${showTypewriter ? '' : (this.placeholder ?? '')}
                        ?disabled=${this.disabled}
                        rows=${this._isCompact ? 1 : 2}
                    ></textarea>
                </div>

                <div class="toolbar">
                    <div class="toolbar__left"></div>
                    <div class="toolbar__right">
                        ${this.isStreaming
                            ? html`
                                  <button
                                      type="button"
                                      class="send-btn"
                                      aria-label="Stop generating"
                                      @click=${this._handleStop}
                                  >
                                      <vm-icon-next name="stop" size="sm"></vm-icon-next>
                                  </button>
                              `
                            : html`
                                  <button
                                      type="submit"
                                      class="send-btn"
                                      aria-label="Send message"
                                      ?disabled=${!canSend}
                                  >
                                      <vm-icon-next name="arrow-up" size="sm"></vm-icon-next>
                                  </button>
                              `}
                    </div>
                </div>
            </form>
            ${this.hideDisclaimer
                ? nothing
                : html`<p class="disclaimer">
                      Vumedi AI can make mistakes. Please double check any answers to verify.
                  </p>`}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-prompt-input': VmPromptInput;
    }
}
